import { useAppConfig } from '@/fetching/api/appConfig';
import { FeatureFlag } from '@/types/featureFlag';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import AccessTimeOutlined from '@mui/icons-material/AccessTimeOutlined';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import DirectionsOutlined from '@mui/icons-material/DirectionsOutlined';
import EventOutlined from '@mui/icons-material/EventOutlined';
import LanguageOutlined from '@mui/icons-material/LanguageOutlined';
import LayersOutlined from '@mui/icons-material/LayersOutlined';
import LocalHospitalOutlined from '@mui/icons-material/LocalHospitalOutlined';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import ManageAccounts from '@mui/icons-material/ManageAccounts';
import MapOutlined from '@mui/icons-material/MapOutlined';
import MonitorOutlined from '@mui/icons-material/MonitorOutlined';
import Person2Outlined from '@mui/icons-material/Person2Outlined';
import QueryStats from '@mui/icons-material/QueryStats';
import RouteOutlined from '@mui/icons-material/RouteOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';

import { DrawerItemConfig } from './types';

/**
 * The navigation items for the global drawer, each with whether the current user can
 * access it. Shared so anything else that links to the same pages offers exactly the
 * ones the drawer would.
 */
export const useGlobalDrawerItems = (): DrawerItemConfig[] => {
    const appConfig = useAppConfig();
    const hasDashboardFeature = useFeatureFlagEnabled(FeatureFlag.DASHBOARD_HOME_VIEW);
    const hasPatientIntakeCapacityPage = useFeatureFlagEnabled(
        FeatureFlag.PATIENT_INTAKE_CAPACITY_PAGE,
    );

    return [
        {
            label: 'Dashboard',
            path: '/',
            exact: true,
            icon: <MonitorOutlined />,
            canAccess: hasDashboardFeature,
            // The dashboard sends users without it on to the visits page.
            canAccessRoute: true,
        },
        {
            label: 'Patients',
            path: '/patients',
            icon: <Person2Outlined />,
            canAccess: appConfig.patient.page,
        },
        {
            label: 'Visits',
            path: '/visits',
            icon: <LanguageOutlined />,
            canAccess: appConfig.visit.page,
        },
        {
            label: 'Clinicians',
            path: '/clinicians',
            icon: <LocalHospitalOutlined />,
            canAccess: appConfig.qhp.page,
        },
        {
            label: 'Reports',
            path: '/reports',
            icon: <DescriptionOutlined />,
            canAccess: appConfig.reports.page,
        },
        {
            label: 'Insights',
            path: '/insights',
            icon: <QueryStats />,
            canAccess: appConfig.insights.page,
        },
        {
            label: 'Route Optimizer',
            path: '/route-optimizer',
            exact: true,
            icon: <RouteOutlined />,
            canAccess: appConfig.route_optimizer.page,
        },
        {
            label: 'Route Optimizer Admin',
            path: '/route-optimizer/admin',
            icon: <DirectionsOutlined />,
            canAccess: appConfig.route_optimizer.preview_clinician_view,
        },
        {
            label: 'Services',
            path: '/services',
            icon: <LayersOutlined />,
            canAccess: appConfig.service.page,
            isDivider: true,
        },
        {
            label: 'Clinician Schedules',
            path: '/schedules',
            icon: <AccessTimeOutlined />,
            canAccess: appConfig.shift.page,
        },
        {
            label: 'Territories',
            path: '/territories',
            icon: <MapOutlined />,
            canAccess: appConfig.territories.page,
        },
        {
            label: 'Service Areas',
            path: '/service-areas',
            icon: <LocationOnOutlined />,
            canAccess: appConfig.service_area.page,
        },
        {
            label: 'Operating Hour Blocks',
            path: '/blocks',
            icon: <EventOutlined />,
            canAccess: appConfig.operating_hour_block.page,
        },
        {
            label: 'User Settings',
            path: '/user-settings',
            icon: <ManageAccounts />,
            canAccess: appConfig.user_settings.page,
            isDivider: true,
        },
        {
            label: 'Settings',
            path: '/settings',
            icon: <SettingsOutlined />,
            canAccess: appConfig.settings.page,
        },
        {
            label: 'Capacity Check',
            path: '/patient-intake',
            icon: <DashboardOutlined />,
            canAccess: hasPatientIntakeCapacityPage,
            isDivider: true,
        },
    ];
};
